import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const CartContext = createContext();
const TAX_RATE = 0.115;
const STORAGE_KEY = 'prwd-cart-items';

const getBrowserStorage = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch (error) {
    console.warn('SessionStorage no disponible, usando localStorage', error);
    try {
      return window.localStorage;
    } catch (err) {
      console.warn('Tampoco se pudo acceder a localStorage', err);
      return null;
    }
  }
};

const createId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    const storage = getBrowserStorage();
    if (!storage) return [];
    const stored = storage.getItem(STORAGE_KEY);
    if (!stored) return [];
    try {
      return JSON.parse(stored);
    } catch (error) {
      console.warn('No se pudo parsear el carrito almacenado:', error);
      storage.removeItem(STORAGE_KEY);
      return [];
    }
  });

  useEffect(() => {
    const storage = getBrowserStorage();
    if (!storage) return;
    try {
      storage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      console.warn('No se pudo persistir el carrito:', error);
    }
  }, [items]);

  const addItem = (item) => {
    if (!item || !item.productId) return;
    setItems((prev) => [...prev, { ...item, id: createId() }]);
  };

  const removeItem = (id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const clearCart = () => setItems([]);

  const { subtotal, taxes, total } = useMemo(() => {
    const sub = items.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
    const taxAmount = sub * TAX_RATE;
    return {
      subtotal: sub,
      taxes: taxAmount,
      total: sub + taxAmount,
    };
  }, [items]);

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        clearCart,
        subtotal,
        taxes,
        total,
        taxRate: TAX_RATE,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
